<template>
  <div class="sidebar-redesign sidebar-client w-100">
    <div class="sidebar-client__nav">
      <template v-for="entry in visibleMenuEntries">
        <router-link
          v-if="entry.type !== 'onboarding'"
          :key="entry.linkTo || entry.type"
          :to="entry.linkTo"
          class="ol-nav-item-link ol-nav-item-link--relative"
          active-class="ol-active"
          @mousedown.native="onItemMousedown(entry)"
        >
          <div class="ol-nav-item">
            <span class="ol-nav-icon">
              <SidebarIcon
                v-if="clientSidebarIconName(entry)"
                :name="clientSidebarIconName(entry)"
              />
              <i v-else :class="entry.icon"></i>
            </span>

            <span class="ol-nav-label">{{ entry.name }}</span>

            <span
              v-if="
                entry.linkTo === '/dashboard' &&
                minlink &&
                $route.fullPath !== '/mysearchs/mysearch' &&
                $route.fullPath !== '/mysearchs'
              "
              class="ol-dashboard-plus"
              @click.prevent.stop="toggleMinLink"
            >
              <strong class="ease-in-out bg-primary pt-1 px-1 mb-2 rounded">
                <i class="icon-plus"></i>
              </strong>
            </span>

            <span
              v-if="
                entry.linkTo === '/dashboard' &&
                minondemand &&
                $route.fullPath !== '/mysearchs/mysearch' &&
                $route.fullPath !== '/mysearchs'
              "
              class="ol-dashboard-plus"
              @click.prevent.stop="toggleMinOndemand"
            >
              <strong class="ease-in-out bg-primary pt-1 px-1 mb-2 rounded">
                <i class="icon-plus"></i>
              </strong>
            </span>
          </div>
        </router-link>

        <router-link
          v-else
          :key="entry.type"
          :to="'/onboarding'"
          class="ol-nav-item-link"
          active-class="ol-active"
          @mousedown.native="navigate('/onboarding')"
        >
          <div class="ol-nav-item">
            <span class="ol-nav-icon">
              <SidebarIcon name="onboarding" />
            </span>

            <span class="ol-nav-label">
              {{ $t("lang.onBoarding.onboarding") }}

              <span
                v-if="
                  user.role_id == 4 &&
                  loadingStatusOnboarding &&
                  user.order_created != '0' &&
                  computedStatusOnboarding != 0
                "
                class="ol-badge"
                >{{ computedStatusOnboarding }}</span
              >

              <span
                v-else-if="computedStatusOnboarding != 0"
                class="ol-badge"
                >{{ computedStatusOnboarding }}</span
              >
            </span>
          </div>
        </router-link>
      </template>
    </div>

    <div class="ol-sidebar-footer ol-sidebar-footer--settings">
      <router-link
        :to="'/settings-account'"
        class="ol-nav-item-link"
        active-class="ol-active"
        @mousedown.native="navigate('/settings-account')"
      >
        <div
          class="ol-nav-item"
          :class="{ 'ol-nav-item--billing': isUserBilling }"
        >
          <span class="ol-nav-icon">
            <SidebarIcon name="settings" />
          </span>

          <span class="ol-nav-label">{{ $t("lang.button.settings") }}</span>
        </div>
      </router-link>
    </div>

    <div class="sidebar-client__spacer" aria-hidden="true"></div>

    <div class="ol-sidebar-footer ol-sidebar-footer--logout">
      <router-link
        :to="'/login'"
        class="ol-nav-item-link"
        @mousedown.native="navigate('/login')"
      >
        <div class="ol-nav-item">
          <span class="ol-nav-icon">
            <SidebarIcon name="logout" :size="16" />
          </span>

          <span class="ol-nav-label">{{ $t("lang.button.logout") }}</span>
        </div>
      </router-link>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from "vuex";
import { tabsMixin } from "@/_mixins/";
import SidebarIcon from "@/assets/icons/sidebar/SidebarIcon.vue";
import { resolveClientSidebarIcon } from "@/assets/icons/sidebar/paths";

export default {
  name: "SidebarClient",

  components: { SidebarIcon },

  mixins: [tabsMixin],

  data() {
    return {
      minlink: false,
      minondemand: false,
      loadingStatusOnboarding: false,
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.authentication.user,
      statusOnboarding: (state) => state.onboarding.statusOnboarding,
    }),

    ...mapGetters("authentication", ["isSaleOrAccountExecutive"]),

    toastNotificationData() {
      return this.$store.state.authentication.toastNotificationData;
    },

    isUserBilling() {
      return (
        this.user &&
        Number(this.user.role_id) === 4 &&
        Number(this.user.team_role_id) === 3
      );
    },

    showClientDocs() {
      return this.hasPermissions(["billing"]);
    },

    computedStatusOnboarding() {
      if (this.hasPermissions("billing")) {
        return this.statusOnboarding.filter((el) => el.value === false).length;
      }

      if (this.hasPermissions("sale")) {
        const consideredStates = ["emailSincronized", "avatar"];

        return this.statusOnboarding

          .filter((e) => consideredStates.includes(e.name))

          .filter((el) => el.value === false).length;
      }

      return 0;
    },

    clientMenuLayout() {
      return [
        { type: "onboarding" },
        {
          linkTo: "/client-space",
          icon: "icon-dashboard",
          roles: [3, 4],
          team_roles: [1, 2, 3],
          afterTrialPeriodAvailable: false,
          name: this.$t("lang.label.dashboard"),
        },  
        {
          linkTo: "/dashboard",
          icon: "icon-dashboard",
          roles: [3, 4],
          team_roles: [1, 2, 3],
          afterTrialPeriodAvailable: false,
        },
        {
          linkTo: "/activity",
          icon: "fa-solid fa-chart-line",
          roles: [3, 4],
          team_roles: [1, 2, 3],
          afterTrialPeriodAvailable: false,
        },

        {
          linkTo: "/orders",
          icon: "icon-barcode",
          roles: [3, 4],
          afterTrialPeriodAvailable: false,
        },

        {
          linkTo: "/businesses",
          icon: "icon-users",
          roles: [3, 4],
          team_roles: [1, 3],
          afterTrialPeriodAvailable: false,
        },

        {
          linkTo: "/my_team",
          icon: "icon-tree",
          name: this.$t("lang.column.users"),
        },

        {
          linkTo: "/ai-meetings-replay",
          icon: "icon-play",
          name: "AI Meetings Replay",
        },

        {
          linkTo: "/playbook",
          icon: "icon-info",
          name: "Playbook",
        },

        {
          linkTo: "/calendar_config",
          icon: "icon-calendar",
          name: "Calendar",
        },

        {
          linkTo: "/blacklist",
          icon: "icon-globe",
          name: "Blacklist",
        },
      ];
    },

    visibleMenuEntries() {
      return this.clientMenuLayout

        .filter((entry) => this.isClientMenuEntryVisible(entry))

        .map((entry) => this.resolveEntryName(entry));
    },
  },

  created() {
    if (Number(this.user.role_id) === 4) {
      this.loadingStatusOnboarding = true;
    }
  },

  methods: {
    ...mapMutations("sidebar", {
      NavigationClicked: "navigationClicked",
    }),

    hasPermissions(perm) {
      return this.$store.getters["authentication/hasPermissions"](perm);
    },

    clientSidebarIconName(entry) {
      return resolveClientSidebarIcon(entry);
    },

    resolveEntryName(entry) {
      if (entry.name) {
        return entry;
      }

      if (entry.linkTo === "/dashboard") {
        const name =
          Number(this.user.role_id) === 4
            ? this.$t("lang.label.meeting")
            : this.$t("lang.menu.reports");

        return { ...entry, name };
      }

      if (entry.linkTo === "/activity") {
        return { ...entry, name: this.$t("lang.menu.activity") };
      }

      if (entry.linkTo === "/orders") {
        return { ...entry, name: this.$t("lang.orders.orders") };
      }

      if (entry.linkTo === "/businesses") {
        return { ...entry, name: this.$t("lang.menu.contacts") };
      }

      return entry;
    },

    onItemMousedown(entry) {
      if (this.checkClientPlan(entry)) {
        this.navigate(entry.linkTo);
      } else {
        this.restrictedAccess();
      }
    },

    navigate(routeTo) {
      const parent = this.$parent;

      const isMobile = parent && parent.isMobile;

      if (isMobile && this.$route.fullPath !== routeTo) {
        const toggle = parent.$refs && parent.$refs.toggleMenu2;

        if (toggle) {
          toggle.click();
        }

        this.$router.push({ path: routeTo });
      }

      if (this.$route.fullPath === routeTo) {
        return;
      }

      if (this.$route.path.includes(routeTo)) {
        this.NavigationClicked();

        this.$router.push({ path: routeTo });
      }
    },

    toggleMinLink() {
      this.minlink = !this.minlink;
    },

    toggleMinOndemand() {
      this.minondemand = !this.minondemand;
    },
  },
};
</script>

<style scoped>
.sidebar-client {
  --ol-sidebar-bg: #fbfbfa;

  --ol-border: #ededec;

  --ol-text: #1a1a18;

  --ol-text-secondary: #6b6b67;

  --ol-text-tertiary: #9a9a96;

  --ol-hover: #f0f0ee;

  --ol-active: #ebebe9;

  --ol-accent: #5054EB;

  --ol-accent-soft: #f3f1fd;

  --ol-radius: 8px;

  flex: 1 1 auto;

  display: flex;

  flex-direction: column;

  min-height: 0;

  width: 100%;

  padding: 0 0 8px;
}

.sidebar-client__nav {
  flex: 0 0 auto;
}

.sidebar-client__spacer {
  flex: 1 1 auto;

  min-height: 8px;
}

.ol-nav-item-link {
  display: block;

  text-decoration: none !important;

  color: inherit;

  margin: 1px 0;
}

.ol-nav-item-link--relative {
  position: relative;
}

.ol-nav-item {
  display: flex;

  align-items: center;

  justify-content: flex-start;

  gap: 11px;

  padding: 8px 9px;

  border-radius: var(--ol-radius);

  font-size: 13.5px;

  color: var(--ol-text);

  cursor: pointer;

  user-select: none;

  text-align: left;
}

.ol-nav-item-link:hover .ol-nav-item {
  background: var(--ol-hover);
}

.ol-nav-item-link.ol-active .ol-nav-item {
  background: var(--ol-active);

  font-weight: 600;
}

.ol-nav-icon {
  width: 18px;

  height: 18px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  color: var(--ol-text-secondary);

  flex-shrink: 0;
}

.ol-nav-item-link.ol-active .ol-nav-icon {
  color: var(--ol-accent);
}

.ol-nav-label {
  flex: 1 1 auto;

  min-width: 0;

  line-height: 1.3;

  text-align: left;

  display: flex;

  align-items: center;

  flex-wrap: wrap;

  gap: 6px;
}

.ol-badge {
  min-width: 18px;

  height: 18px;

  padding: 0 5px;

  border-radius: 9px;

  background: var(--ol-accent);

  color: #fff;

  font-size: 11px;

  font-weight: 600;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  flex-shrink: 0;
}

.ol-dashboard-plus {
  position: absolute;

  right: 8px;

  top: 50%;

  transform: translateY(-50%);

  cursor: pointer;

  z-index: 1;
}

.ol-sidebar-footer {
  flex: 0 0 auto;
}

.ol-sidebar-footer--settings {
  border-top: none;
  padding-top: 0;
}

.ol-sidebar-footer--logout {
  border-top: 1px solid var(--ol-border);
  padding-top: 10px;
  padding-bottom: 4px;
}

.ol-sidebar-footer--logout .ol-nav-item,
.ol-sidebar-footer--logout .ol-nav-icon {
  color: var(--ol-text-secondary);
}

.ol-sidebar-footer--logout .ol-nav-item-link:hover .ol-nav-item {
  background: var(--ol-hover);
  color: var(--ol-text);
}

.ol-sidebar-footer--logout .ol-nav-item-link:hover .ol-nav-icon {
  color: var(--ol-text-secondary);
}
</style>
